import { TurnAroundTimeMinMedianMax } from "modules/PortPredictor/hooks/useFetchPortTurnAroundTime"
import {
  PortPredictorTurnAroundTimeData,
  VesselActivity,
} from "modules/PortPredictor/hooks/usePortPredictorAPI"
import moment from "moment"
import { OverlayTrigger, Popover } from "react-bootstrap"
import { VesselSegment } from "user/userService"
import {
  PortPredictorPortTypeSelectOption,
  portTypes,
} from "./PortPredictorArrivalInputs"
import { TypeOfTurnAroundTimeData } from "./PortOrTerminalTurnAroundTimeCheckboxes"
import { PortType } from "modules/PortPredictor/portPredictorReducer"
import { PortHoliday } from "modules/PortHolidays/usePortHolidayAPI"
import { roundUpPortTurnAroundTime } from "modules/PortPredictor/helpers/utils"
import { median as findMedian } from "simple-statistics"

export const renderTooltipForLast10 = ({
  segment,
  cargo,
  portType,
  portName,
  terminalName,
  typeOfTurnAroundTimeData,
}: {
  segment: VesselSegment
  cargo: string
  portType: PortPredictorPortTypeSelectOption
  portName: string
  terminalName?: string
  typeOfTurnAroundTimeData?: TypeOfTurnAroundTimeData
}) => {
  const activity =
    portType.activity === VesselActivity.Discharge ? "discharging" : "loading"
  const portOrTerminalName =
    typeOfTurnAroundTimeData === TypeOfTurnAroundTimeData.UsePortData
      ? portName
      : terminalName
  return (
    <Popover id="popover-basic">
      <Popover.Body>{`Last 10 ${segment} ships ${activity} ${cargo} at ${portOrTerminalName} `}</Popover.Body>
    </Popover>
  )
}

export const renderHolidayWarning = () => {
  return (
    <Popover id="popover-holiday-alert">
      <Popover.Body>
        <span style={{ fontWeight: "bold" }}>Holiday Alert: </span> Refer to
        calendar and holiday list. Possible delays or limited port operations.
        Consider adjusting plans.
      </Popover.Body>
    </Popover>
  )
}

export function getLaycanWarningBadge({
  portType,
  laycanStart,
  laycanEnd,
  arrivalDate,
}: {
  portType: PortType
  laycanEnd: string
  laycanStart: string
  arrivalDate: string
}) {
  const label = portType === PortType.Discharge ? "window" : "laycan"
  const end = moment(laycanEnd)
  const start = moment(laycanStart)
  const arrival = moment(arrivalDate)

  // If start date - arrival date is positive, start date is bigger
  // than arrival date, therefore vessel has arrived early.

  if (start.diff(arrival) > 0) {
    const daysBeforeStart = start.diff(arrival, "days")
    return (
      <div className="">
        <span className="PortPredictor_WarningFontColor">
          {daysBeforeStart} {daysBeforeStart > 1 ? "days" : "day"}
        </span>
        <span className="ms-1">before {label} start</span>
      </div>
    )
  }

  if (end.diff(arrival) < 0) {
    const daysAfterLaycanEnd = end.diff(arrival, "days")
    const displayDaysAfterLaycanEnd = Math.abs(daysAfterLaycanEnd)
    return (
      <div className="">
        <span className="PortPredictor_DangerFontColor">
          {displayDaysAfterLaycanEnd}{" "}
          {displayDaysAfterLaycanEnd > 1 ? "days" : "day"}
        </span>
        <span> after cancelling</span>
      </div>
    )
  }

  if (end.diff(arrival) === 0) {
    return (
      <div className="">
        <span className="PortPredictor_WarningFontColor">0 days </span>
        <span>to cancelling</span>
      </div>
    )
  }
}

export function getArrivalDateClass({
  laycanStart,
  laycanEnd,
  arrivalDate,
}: {
  laycanStart: string | undefined
  laycanEnd: string | undefined
  arrivalDate: string | undefined
}) {
  if (!laycanStart || !laycanEnd || !arrivalDate) {
    return
  }

  const end = moment(laycanEnd)
  const start = moment(laycanStart)
  const arrival = moment(arrivalDate)

  if (end.diff(arrival) === 0) {
    return "PortPredictor_WarningFontColor"
  }

  if (end.diff(arrival, "days") === 1) {
    return "PortPredictor_WarningFontColor"
  }

  if (start.diff(arrival) > 0) {
    return "PortPredictor_WarningFontColor"
  }

  if (end.diff(arrival) < 0) {
    return "PortPredictor_DangerFontColor"
  }

  return "PortPredictor_SuccessFontColor"
}

export function getPortType(portType: PortType | undefined) {
  if (portType === PortType.Discharge) {
    return "Discharge Port"
  }
  if (portType === PortType.Load) {
    return "Load Port"
  }
  if (portType === PortType.Other) {
    return "Other/Bunkering"
  }
  return "Unknown Port Type"
}

export function getActivityFromPortType(portType: PortType) {
  return portTypes.find((opt) => {
    return opt.value === portType
  }) as PortPredictorPortTypeSelectOption
}

export function findNearbyHolidaysForEtaEtd({
  arrivalDate,
  holidays,
  turnAroundTime,
}: {
  arrivalDate: string | undefined
  holidays: PortHoliday[] | undefined
  turnAroundTime: number | undefined
}) {
  if (!holidays || holidays.length < 1 || !turnAroundTime) {
    return
  }
  const startRange = moment(arrivalDate).subtract(4, "days")
  const endRange = moment(arrivalDate).add(turnAroundTime + 4, "days")

  // Filter holidays based on the range
  return holidays.filter((holiday) => {
    const holidayStart = moment(holiday.start_date)
    const holidayEnd = moment(holiday.end_date)
    return (
      holidayStart.isBetween(startRange, endRange, "day", "[]") ||
      holidayEnd.isBetween(startRange, endRange, "day", "[]")
    )
  })
}
export function findNearbyHolidaysForLaycan({
  triggerDate,
  holidays,
  rangeInDays,
}: {
  triggerDate: string | undefined
  holidays: PortHoliday[] | undefined
  rangeInDays: number
}) {
  if (!triggerDate || !holidays) {
    return
  }

  console.log(holidays)
  // Convert the range to milliseconds
  const rangeInMs = rangeInDays * 24 * 60 * 60 * 1000

  // Convert the triggerDate to a Date object
  const triggerDateObj = new Date(triggerDate).getTime()

  // Filter holidays to find those within the range
  const result = holidays.filter((holiday) => {
    const holidayStartDate = new Date(holiday.start_date).getTime()
    const holidayEndDate = new Date(holiday.end_date).getTime()

    // Check if the triggerDate is within the range
    return (
      Math.abs(triggerDateObj - holidayStartDate) <= rangeInMs ||
      Math.abs(triggerDateObj - holidayEndDate) <= rangeInMs
    )
  })

  return result
}

export function renderDaysSinceArrivalPopup(daysSinceArrival: number) {
  if (daysSinceArrival >= 20) {
    return (
      <Popover id="popover-fould-risk-high">
        <Popover.Body>
          Foul Risk:{" "}
          <span style={{ color: "#f14727", fontWeight: "bold" }}>High</span>.
          May impact vessel performance & ETA to the next port(s).
        </Popover.Body>
      </Popover>
    )
  }
  return (
    <Popover id="popover-foul-risk-medium">
      <Popover.Body>
        Foul Risk:{" "}
        <span style={{ color: "#f14727", fontWeight: "bold" }}>Medium</span>.
        May impact vessel performance & ETA to the next port(s).
      </Popover.Body>
    </Popover>
  )
}

export function renderDaysSinceArrivalAlert(
  daysSinceArrival: number | "" | undefined
) {
  if (!daysSinceArrival) {
    return
  }
  if (daysSinceArrival >= 20) {
    return (
      <OverlayTrigger overlay={renderDaysSinceArrivalPopup(daysSinceArrival)}>
        <div>
          <i className="ms-1 ti-alert me-1 PortPredictor_AlertDanger" />
        </div>
      </OverlayTrigger>
    )
  }
  if (daysSinceArrival >= 10 && daysSinceArrival <= 20) {
    return (
      <OverlayTrigger overlay={renderDaysSinceArrivalPopup(daysSinceArrival)}>
        <div>
          <i className="ti-alert ms-1 me-1 PortPredictor_AlertWarning" />
        </div>
      </OverlayTrigger>
    )
  }
  return <></>
}

export function noRecentVesselsRepresentOverallMedianTime({
  turnAroundTimeData,
  turnAroundTimeMinMedianMax,
}: {
  turnAroundTimeData: PortPredictorTurnAroundTimeData[]
  turnAroundTimeMinMedianMax: TurnAroundTimeMinMedianMax | undefined
}) {
  if (!turnAroundTimeMinMedianMax) {
    return
  }

  const now = moment()
  const daysAgo = now.subtract(7, "days")
  const recentVessels = turnAroundTimeData.filter((vessel) =>
    moment(vessel.datetime_to).isAfter(daysAgo)
  )

  if (recentVessels.length < 1) {
    return
  }

  const recentVesselsTurnAroundTimes = recentVessels.map(
    (vessel) => vessel.days_in_port
  )

  const hasRecentVesselExceedingMedian = recentVesselsTurnAroundTimes.some(
    (daysInPort) => daysInPort > turnAroundTimeMinMedianMax.median.value
  )

  return hasRecentVesselExceedingMedian
}

export function hasNoRecentDeparture({
  turnAroundTimeData,
}: {
  turnAroundTimeData: PortPredictorTurnAroundTimeData[]
}) {
  const now = moment()
  //All departures are more than 1 month ago
  const oneMonthAgo = now.subtract(1, "month")
  const mostRecentDeparture = turnAroundTimeData
    .map((vessel) => moment(vessel.datetime_to))
    .sort((a, b) => b.valueOf() - a.valueOf())[0] // Sort descending and take the first

  return mostRecentDeparture.isBefore(oneMonthAgo)
}

export function renderWarningForLast10Vessels({
  turnAroundTimeData,
  turnAroundTimeMinMedianMax,
}: {
  turnAroundTimeData: PortPredictorTurnAroundTimeData[]
  turnAroundTimeMinMedianMax: TurnAroundTimeMinMedianMax | undefined
}) {
  const isNoRecentDeparture = hasNoRecentDeparture({ turnAroundTimeData })
  const isNoRecentVesselsRepresentOverallMedianTime =
    noRecentVesselsRepresentOverallMedianTime({
      turnAroundTimeData,
      turnAroundTimeMinMedianMax,
    })

  if (isNoRecentDeparture) {
    return (
      <OverlayTrigger
        overlay={renderWarningLast10VesselsPopup({
          message: `Listed vessels are from previous months. Consider switching to a different cargo group. Contact the agent for further details and guidance.`,
        })}
      >
        <div>
          <i className="ms-1 ti-alert me-1 PortPredictor_AlertWarning" />
        </div>
      </OverlayTrigger>
    )
  }
  if (
    turnAroundTimeMinMedianMax &&
    isNoRecentVesselsRepresentOverallMedianTime
  ) {
    return (
      <OverlayTrigger
        overlay={renderWarningLast10VesselsPopup({
          message: `One or more vessels in the last 7 days have exceeded the median time of ${turnAroundTimeMinMedianMax.median.value} days. Check with agents and consider planning for delays.`,
        })}
      >
        <div>
          <i className="ms-1 ti-alert me-1 PortPredictor_AlertWarning" />
        </div>
      </OverlayTrigger>
    )
  }
}

export function renderWarningLast10VesselsPopup({
  message,
}: {
  message: string
}) {
  return (
    <Popover id="popover-last10-alert">
      <Popover.Body>{message}</Popover.Body>
    </Popover>
  )
}
